import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Share2, Copy, Check,
  FileSignature, QrCode, Zap, ShieldCheck, Download, Stethoscope, UserRound,
  ClipboardCheck, Layers,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/auto-prescriptions';
const SHARE_MESSAGE = (
  `DocSite just made prescriptions dramatically simpler.\n\n` +
  `Every time your doctor seals your Digital Health File, a signed, verifiable prescription is auto-generated and delivered inside the same link. No extra clicks. No paper. No lost slips.\n\n` +
  `• Cryptographically signed — every prescription carries a QR anyone can scan to verify\n` +
  `• Pharmacy-ready A4 PDF with brand, molecule, dose, duration and quantity\n` +
  `• Auto-linked to your Digital Health File — one link, everything you need\n\n` +
  `Read more: ${SHARE_URL}`
);

const PATIENT_FEATURES = [
  {
    icon: Download,
    title: 'One link, everything included',
    body: 'The same WhatsApp / SMS link your doctor sent for your Digital Health File now also carries your prescription. Open the link, and you see a "Digital Prescription" card with a Download PDF button — right next to your medicines and follow-up plan.',
    color: 'sky',
  },
  {
    icon: QrCode,
    title: 'Any pharmacist can verify it in one scan',
    body: 'A QR code printed on the prescription lets any pharmacist confirm it is genuine, unaltered, and issued by a real DocSite-verified doctor. No more calls to the clinic to check if the slip is real.',
    color: 'emerald',
  },
  {
    icon: FileSignature,
    title: 'A pharmacy-ready A4 PDF',
    body: 'Brand name, molecule, strength, dose, frequency, duration, quantity — every field a pharmacist needs. Plus clear patient instructions and a friendly note in plain language. Print it or show it on your phone.',
    color: 'violet',
  },
  {
    icon: ShieldCheck,
    title: 'Cryptographically signed and immutable',
    body: 'The moment your doctor seals it, the prescription is fingerprinted using SHA-256 and locked. Any tampering — even a single character — breaks the fingerprint and the verification page will say "not genuine".',
    color: 'amber',
  },
];

const DOCTOR_FEATURES = [
  {
    icon: Zap,
    title: 'Zero extra clicks',
    body: 'The prescription is generated automatically at the moment you seal a Digital Health File that contains medicines. You do not click a separate button. You do not chase a separate SMS. You do not remember to send it later.',
    color: 'sky',
  },
  {
    icon: Stethoscope,
    title: 'Your identity, sealed at visit time',
    body: 'Your name, qualifications, registration number, specialty, signature and clinic address are all captured immutably at the moment of the visit. If you later update your profile, old prescriptions still show the correct historical state — the medico-legally correct behaviour.',
    color: 'emerald',
  },
  {
    icon: ClipboardCheck,
    title: 'The prescription is a downstream artifact of the DHF',
    body: 'Every prescription is auto-linked to a sealed Digital Health File — same patient snapshot, same diagnosis, same follow-up plan. No orphan Rxs, no mismatched details between the file and the slip.',
    color: 'violet',
  },
  {
    icon: Layers,
    title: 'Regular + SOS medicines auto-split',
    body: 'Medicines flagged as SOS / PRN / "as needed" automatically move into a separate "Part 2 : SOS" section on the printout, distinct from regular / essential medicines. Cleaner communication for the patient and pharmacy.',
    color: 'rose',
  },
];

const COLOR_MAP = {
  sky:     { bg: 'bg-sky-100',     border: 'border-sky-200',     icon: 'text-sky-700' },
  emerald: { bg: 'bg-emerald-100', border: 'border-emerald-200', icon: 'text-emerald-700' },
  violet:  { bg: 'bg-violet-100',  border: 'border-violet-200',  icon: 'text-violet-700' },
  amber:   { bg: 'bg-amber-100',   border: 'border-amber-200',   icon: 'text-amber-700' },
  rose:    { bg: 'bg-rose-100',    border: 'border-rose-200',    icon: 'text-rose-700' },
};

function FeatureCard({ f }) {
  const c = COLOR_MAP[f.color] || COLOR_MAP.sky;
  const Ico = f.icon;
  return (
    <div className={`rounded-2xl bg-white border ${c.border} p-5 flex gap-4 items-start hover:shadow-sm transition-shadow`}>
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
        <Ico size={20} className={c.icon} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {f.title}
        </h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.body}</p>
      </div>
    </div>
  );
}

export default function AutoRxLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — Auto-Prescriptions', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="auto-rx-launch-page">
      <Helmet>
        <title>Auto-Prescriptions — one-click signed digital Rx | DocSite</title>
        <meta name="description" content="Every DocSite Digital Health File now auto-issues a signed, cryptographically verifiable prescription. Delivered inside the same patient link. No extra clicks." />
        <meta property="og:title" content="DocSite — Auto-Prescriptions" />
        <meta property="og:description" content="Every sealed DHF now delivers a pharmacy-ready, signed, QR-verifiable digital prescription. Automatically." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="og:image" content="https://docsite.care/auto-rx-launch-preview.png" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="back-to-whats-new">
          <ArrowLeft size={12} /> All updates
        </Link>

        {/* HERO */}
        <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-emerald-50 border border-sky-200 p-6 sm:p-10 mb-6">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-sky-600 text-white">
            <Sparkles size={10} /> Just Launched · 4 Aug 2026
          </span>
          <div className="mt-5 flex items-center gap-4">
            <div className="relative shrink-0">
              <span className="absolute inset-0 rounded-2xl bg-sky-400/25 animate-ping" aria-hidden="true"></span>
              <span className="relative inline-flex w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-sky-600 via-emerald-500 to-amber-500 shadow-lg items-center justify-center" data-testid="auto-rx-icon">
                <FileSignature size={36} className="text-white sm:hidden" strokeWidth={2} />
                <FileSignature size={44} className="text-white hidden sm:block" strokeWidth={2} />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-700/80">Digital Prescription · What&apos;s new</p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">One-click · Signed · Pharmacy-ready · QR-verifiable</p>
            </div>
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold text-slate-900 mt-5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Every Digital Health File now issues a signed digital prescription — automatically.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-700 leading-relaxed">
            The moment your doctor seals your Digital Health File, a fully-signed A4 prescription is
            auto-generated and delivered inside the same link. No extra clicks. No paper slips. No
            separate SMS. And any pharmacist in India can verify it is genuine by scanning the QR code.
          </p>
        </div>

        {/* PRESCRIPTION PRINTOUT PREVIEW */}
        <div className="rounded-3xl bg-white border border-slate-200 p-4 sm:p-6 mb-6 shadow-sm" data-testid="rx-preview">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sample Prescription Printout</p>
              <p className="text-sm text-slate-700 mt-0.5 font-medium">Generated the instant a Digital Health File is sealed</p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold uppercase tracking-wider">
              <ShieldCheck size={11} /> Signed &amp; Verifiable
            </span>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
            <img
              src="/auto-rx-launch-preview.png"
              alt="Sample DocSite Digital Prescription — pharmacy-ready A4 layout with doctor identity, patient details, medicines, QR verification, digital signature and stamp"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="rx-preview-image"
            />
          </div>
          <div className="grid sm:grid-cols-4 gap-3 mt-4">
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800">Header</p>
              <p className="text-xs text-slate-700 mt-1">Doctor identity + reg. no. captured from the DHF snapshot at visit time</p>
            </div>
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-800">Medicines</p>
              <p className="text-xs text-slate-700 mt-1">Regular + SOS auto-split. Brand, molecule, dose, duration, quantity — all in one row</p>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">QR Verify</p>
              <p className="text-xs text-slate-700 mt-1">Pharmacist scans → sees issuer, issue date, revocation status. Cryptographic proof</p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Signature &amp; Stamp</p>
              <p className="text-xs text-slate-700 mt-1">Doctor&apos;s digital signature and clinic stamp — auto-defaulted or uploaded</p>
            </div>
          </div>
        </div>

        {/* FOR PATIENTS */}
        <div className="mb-4 flex items-center gap-2 mt-8" data-testid="section-for-you">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-sky-200 text-sky-800 text-[11px] font-bold uppercase tracking-wider">
            <UserRound size={11} /> For Patients
          </span>
          <span className="text-xs text-slate-500">— what changes the next time your doctor issues a Digital Health File</span>
        </div>
        <div className="space-y-3">
          {PATIENT_FEATURES.map((f, idx) => (
            <div key={idx} data-testid={`patient-feature-${idx}`}>
              <FeatureCard f={f} />
            </div>
          ))}
        </div>

        {/* FOR DOCTORS */}
        <div className="mt-10 mb-4 flex items-center gap-2" data-testid="section-for-doctors">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800 text-[11px] font-bold uppercase tracking-wider">
            <Stethoscope size={11} /> For Doctors
          </span>
          <span className="text-xs text-slate-500">— what changes for you as a DocSite doctor</span>
        </div>
        <div className="space-y-3">
          {DOCTOR_FEATURES.map((f, idx) => (
            <div key={idx} data-testid={`doctor-feature-${idx}`}>
              <FeatureCard f={f} />
            </div>
          ))}
        </div>

        {/* VERIFY CTA */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-2 border-emerald-200 p-6 relative overflow-hidden" data-testid="section-verify">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-emerald-100 opacity-40" aria-hidden="true"></div>
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest">
              <QrCode size={11} /> DocSite Verified Rx
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A public verify endpoint — for any pharmacist, insurer or patient.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed max-w-2xl">
              Drop any DocSite prescription PDF at{' '}
              <Link to="/rx-verify" className="text-emerald-800 font-semibold underline">
                docsite.care/rx-verify
              </Link>{' '}
              and see instantly whether it is genuine, revoked, or unknown. No account, no login,
              no paperwork. Cryptographic proof, in one click.
            </p>
            <Link
              to="/rx-verify"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              data-testid="rx-verify-cta"
            >
              Try the verify tool <QrCode size={14} />
            </Link>
          </div>
        </div>

        {/* SHARE */}
        <div className="mt-8 rounded-3xl bg-slate-900 text-white p-6 sm:p-8" data-testid="share-block">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-300">Spread the word</p>
          <h3 className="text-xl sm:text-2xl font-semibold mt-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Know a doctor tired of writing paper prescriptions?
          </h3>
          <p className="text-sm text-slate-300 mt-2">
            Share this update. Every DocSite doctor gets auto-prescriptions turned on the moment they seal their next Digital Health File.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold"
               data-testid="share-whatsapp">
              <Share2 size={14} /> Share on WhatsApp
            </a>
            <button onClick={nativeShare}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white hover:bg-slate-100 text-slate-900 text-sm font-bold"
                    data-testid="share-native">
              <Share2 size={14} /> Share…
            </button>
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold border border-slate-700"
                    data-testid="share-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-10">
          Published 4 August 2026 · DocSite Auto-Prescriptions v1
        </p>
      </div>
    </div>
  );
}
